// OfferLetterModal — shows the offer letter as a rendered DOCX preview (via mammoth HTML).
// Falls back to the AI-generated text if no DOCX template is uploaded.

import React, { useEffect, useState } from "react";
import { X, Printer, Send, FileDown, Loader2, AlertCircle } from "lucide-react";
import api from '../../../services/api';

function markdownToHtml(md) {
  if (!md) return "";
  // Strip any leftover code fences
  md = md.replace(/^```(?:markdown)?\s*\n?/i, '').replace(/\n?```\s*$/i, '');
  let html = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, "<h3 style='font-size:1rem;font-weight:700;margin:14px 0 5px'>$1</h3>")
    .replace(/^## (.+)$/gm,  "<h2 style='font-size:1.1rem;font-weight:700;margin:18px 0 6px'>$1</h2>")
    .replace(/^# (.+)$/gm,   "<h1 style='font-size:1.3rem;font-weight:700;margin:20px 0 8px'>$1</h1>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g,     "<em>$1</em>");
  html = html.replace(/(^[-*] .+$(\n[-*] .+$)*)/gm, (block) => {
    const items = block.split("\n").filter(l => /^[-*] /.test(l))
      .map(l => `<li style='margin:3px 0'>${l.slice(2)}</li>`).join("");
    return `<ul style='padding-left:20px;margin:8px 0'>${items}</ul>`;
  });
  html = html.split("\n").map(line => {
    if (!line.trim()) return "<div style='height:6px'></div>";
    if (/^<(h[123]|ul|li|div)/.test(line)) return line;
    return `<p style='margin:6px 0;line-height:1.7'>${line}</p>`;
  }).join("\n");
  return html;
}

export default function OfferLetterModal({ isOpen, onClose, content, company, onSend, canSend, resumeId }) {
  const [previewHtml, setPreviewHtml] = useState(null);  // filled DOCX → HTML
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError]   = useState("");

  // Fetch DOCX-rendered HTML whenever the modal opens with a resumeId
  useEffect(() => {
    if (!isOpen || !resumeId) return;
    setPreviewHtml(null);
    setPreviewError("");
    setPreviewLoading(true);
    api.get(`/offer-letters/resumes/${resumeId}/preview-offer-html`)
      .then(r => {
        if (r.data?.success) setPreviewHtml(r.data.html);
        else setPreviewError(r.data?.message || "Preview unavailable");
      })
      .catch(e => {
        const msg = e.response?.data?.message || e.message || "Preview failed";
        console.error('[OfferLetterModal] preview fetch failed:', msg);
        setPreviewError(msg);
      })
      .finally(() => setPreviewLoading(false));
  }, [isOpen, resumeId]);

  if (!isOpen) return null;

  const handleDownloadDocx = async () => {
    if (!resumeId) return;
    try {
      const resp = await api.get(`/offer-letters/resumes/${resumeId}/download-offer-docx`, { responseType: 'blob' });
      const url  = URL.createObjectURL(resp.data);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'offer_letter.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.message || 'DOCX download failed. Ensure a .docx template is uploaded.');
    }
  };

  const handlePrint = () => {
    const printContent = previewHtml
      ? `<div style="max-width:720px;margin:40px auto;padding:0 40px 60px;font-family:sans-serif">${previewHtml}</div>`
      : buildFallbackHtml(content, company);

    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Offer Letter</title>
<style>
  body{margin:0;padding:0;font-family:Calibri,sans-serif;font-size:14px;color:#1a1a1a}
  img{max-width:100%;height:auto}
  table{border-collapse:collapse;width:100%}
  td,th{padding:4px 8px}
  @media print{@page{margin:20mm}}
</style>
</head><body>${printContent}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  // ── Iframe srcdoc for isolated rendering ──────────────────────────────────
  const iframeSrc = previewHtml
    ? `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  body{margin:0;padding:32px 48px;font-family:Calibri,'Times New Roman',serif;font-size:13pt;color:#1a1a1a;line-height:1.6}
  img{max-width:100%;height:auto}
  table{border-collapse:collapse;width:100%}
  td,th{padding:6px 10px;vertical-align:top}
  p{margin:6px 0}
  h1,h2,h3,h4{margin:14px 0 6px;color:#111}
  strong,b{font-weight:700}
  ul,ol{margin:8px 0;padding-left:22px}
  li{margin:3px 0}
</style>
</head><body>${previewHtml}</body></html>`
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 font-outfit">
      <div className="bg-white rounded-meraki shadow-xl w-full max-w-4xl max-h-[94vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-meraki-soft-blue">
          <h2 className="text-lg font-bold text-meraki-indigo">Offer Letter Preview</h2>
          <button onClick={onClose} className="text-meraki-graphite/40 hover:text-meraki-graphite">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden relative">

          {/* Loading */}
          {previewLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              <p className="text-sm text-gray-500">Generating preview…</p>
            </div>
          )}

          {/* DOCX rendered preview in sandboxed iframe */}
          {!previewLoading && iframeSrc && (
            <iframe
              title="Offer Letter Preview"
              srcDoc={iframeSrc}
              className="w-full h-full border-0"
              style={{ minHeight: '65vh' }}
              sandbox="allow-same-origin"
            />
          )}

          {/* Fallback: no template uploaded or error */}
          {!previewLoading && !iframeSrc && (
            <div className="h-full overflow-y-auto p-8">
              {previewError && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{previewError} — showing AI-generated text instead.</span>
                </div>
              )}
              {/* Company letterhead fallback */}
              {(company?.logo || company?.name) && (
                <div className="text-center mb-8 pb-6 border-b border-slate-200">
                  {company.logo && (
                    <img src={company.logo} alt={company.name || "Company"}
                      className="mx-auto max-h-20 max-w-[200px] object-contain mb-3"
                      onError={e => { e.target.style.display = "none"; }} />
                  )}
                  {company.name && <div className="font-bold text-meraki-indigo text-lg">{company.name}</div>}
                  {company.website && <div className="text-xs text-slate-400 mt-1">{company.website}</div>}
                </div>
              )}
              <div
                className="text-[14px] leading-relaxed text-gray-800 space-y-2"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(content || '') }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-meraki-soft-blue flex justify-between items-center">
          <button onClick={onClose} className="text-sm text-meraki-graphite/60 hover:text-meraki-graphite">Close</button>
          <div className="flex gap-2">
            {resumeId && (
              <button onClick={handleDownloadDocx}
                className="btn-meraki-secondary text-sm flex items-center gap-2">
                <FileDown className="w-4 h-4" /> Download DOCX
              </button>
            )}
            <button onClick={handlePrint}
              className="btn-meraki-secondary text-sm flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print / PDF
            </button>
            {canSend && onSend && (
              <button onClick={onSend}
                className="btn-meraki text-sm flex items-center gap-2">
                <Send className="w-4 h-4" /> Send to Candidate
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function buildFallbackHtml(content, company) {
  const logo = company?.logo
    ? `<div style="text-align:center;margin-bottom:24px"><img src="${company.logo}" style="max-height:80px;max-width:200px;object-fit:contain"/></div>`
    : "";
  const name = company?.name ? `<div style="text-align:center;font-weight:700;font-size:1.1rem;margin-bottom:4px">${company.name}</div>` : "";
  const site = company?.website ? `<div style="text-align:center;font-size:0.8rem;color:#666;margin-bottom:24px">${company.website}</div>` : "";
  const body = `<pre style="white-space:pre-wrap;font-family:Calibri,sans-serif;font-size:13pt;line-height:1.6;color:#1a1a1a">${(content || "").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</pre>`;
  return `<div style="max-width:720px;margin:40px auto;padding:0 40px 60px">${logo}${name}${site}<hr style="border:none;border-top:1px solid #eee;margin:20px 0"/>${body}</div>`;
}
